<script setup lang="ts">
   import { definePageMeta, useHead } from '#imports';
   import { computed } from 'vue';
   import { usePublicGalleries } from '~/composables/usePublicGalleries';

   definePageMeta({
      layout: 'wow-polo',
   });

   useHead({
      title: 'Gallery — WOW Polo Series',
   });

   const config = useRuntimeConfig();
   const portal = config.public.portalBrand as string;
   const { galleriesData, isLoading } = usePublicGalleries(portal);

   const galleries = computed(() => galleriesData.value?.data ?? []);
</script>

<template>
   <div>
      <!-- Hero -->
      <section
         class="wow-gallery-hero relative h-[320px] bg-cover bg-center bg-no-repeat">
         <div class="absolute inset-0 bg-black/50" />
         <div
            class="relative flex h-full flex-col items-center justify-center px-6 text-center">
            <h1
               class="mb-4 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
               Gallery
            </h1>
            <p class="max-w-xl text-lg text-white/90">
               Photo galleries from WOW Polo Series events
            </p>
         </div>
      </section>

      <!-- Gallery Grid -->
      <section class="bg-white py-16">
         <div class="mx-auto max-w-6xl px-6">
            <!-- Loading -->
            <div v-if="isLoading" class="flex justify-center py-12">
               <div
                  class="size-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#5cc8d9]" />
            </div>

            <!-- Empty state -->
            <p
               v-else-if="galleries.length === 0"
               class="py-12 text-center text-gray-500">
               No galleries available yet.
            </p>

            <!-- Gallery cards -->
            <div v-else class="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
               <NuxtLink
                  v-for="gallery in galleries"
                  :key="gallery.id"
                  :to="`/gallery/${gallery.slug}`"
                  class="group overflow-hidden rounded-xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md">
                  <!-- Thumbnail -->
                  <div class="group relative aspect-[4/3] bg-gray-100">
                     <template v-if="gallery.thumbnail_url">
                        <img
                           :src="gallery.thumbnail_url"
                           :alt="gallery.title"
                           class="size-full object-cover"
                           loading="lazy" />
                        <p
                           v-if="gallery.short_description"
                           class="absolute inset-0 flex size-full items-center justify-center bg-gray-900/50 text-white opacity-0 transition group-hover:opacity-100">
                           {{ gallery.short_description }}
                        </p>
                     </template>
                     <div
                        v-else
                        class="flex h-full items-center justify-center text-gray-400">
                        <svg
                           class="size-12"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                        </svg>
                     </div>
                  </div>

                  <!-- Card info -->
                  <div class="relative p-4">
                     <h2
                        class="font-semibold text-gray-900 group-hover:text-[#5cc8d9]">
                        {{ gallery.title }}
                     </h2>
                     <p class="mt-1 text-sm text-gray-500">
                        {{ gallery.image_count }}
                        {{ gallery.image_count === 1 ? 'photo' : 'photos' }}
                     </p>
                  </div>
               </NuxtLink>
            </div>
         </div>
      </section>
   </div>
</template>

<style scoped>
   .wow-gallery-hero {
      background-image: url('/img/home-page-hero-bg.webp');
   }
</style>
